import { reportingIdentifier } from 'src/queries/reporting/reporting.identifier'
import { REPORTING_AGENT_VERSION } from 'src/queries/reportingAgent/reportingAgent.models'

export const object = (
  value: unknown,
  name: string,
): Record<string, unknown> => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`${name} must be an object`)
  }
  return value as Record<string, unknown>
}

export const exact = (
  value: Record<string, unknown>,
  keys: readonly string[],
  name: string,
): void => {
  const allowed = new Set(keys)
  const unknown = Object.keys(value).filter((key) => !allowed.has(key))
  if (unknown.length > 0)
    throw new Error(`${name} has unknown fields: ${unknown.join(', ')}`)
}

export const string = (value: unknown, name: string): string => {
  if (typeof value !== 'string') throw new Error(`${name} must be a string`)
  return value
}

export const version = (value: unknown, name: string): void => {
  if (value !== REPORTING_AGENT_VERSION)
    throw new Error(`${name}.version is unsupported`)
}

export const nullableIdentifier = (
  value: unknown,
  name: string,
): string | null => (value === null ? null : reportingIdentifier(value, name))

export const isoDate = (value: unknown, name: string): string => {
  const result = string(value, name)
  if (
    result.startsWith('0000-') ||
    !TIMESTAMP_PATTERN.test(result) ||
    Number.isNaN(Date.parse(result))
  )
    throw new Error(`${name} must be a canonical UTC timestamp`)
  const parsed = new Date(result)
  if (parsed.toISOString().slice(0, 19) !== result.slice(0, 19))
    throw new Error(`${name} must be a real UTC timestamp`)
  return result
}

export const safeRelativeUrl = (value: unknown, name: string): string => {
  const result = string(value, name)
  if (
    !result.startsWith('/') ||
    result.startsWith('//') ||
    result.includes('\\') ||
    result.split('/').some((part) => part === '..') ||
    /^[a-z][a-z\d+.-]*:/i.test(result)
  ) {
    throw new Error(`${name} must be a safe relative URL`)
  }
  return result
}

export const eventsUrl = (value: unknown, name: string): string => {
  const result = string(value, name)
  const match = /^\/api\/v1\/agents\/runs\/([^/]+)\/events\/stream$/.exec(
    result,
  )
  if (!match)
    throw new Error(`${name} must match the customer event stream URL shape`)
  reportingIdentifier(match[1], `${name}.run_id`)
  return result
}

export const TIMESTAMP_PATTERN =
  /^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}(\.[0-9]{1,6})?Z$/
export const PROGRESS_STAGES = new Set([
  'interpreting_question',
  'building_query',
  'running_query',
  'preparing_visualization',
])
export const OPERATIONAL_FAILURES = {
  query_too_expensive: {
    retryable: false,
    message: 'This report is too expensive to run as requested.',
  },
  result_too_large: {
    retryable: false,
    message: 'This report returns too much data. Narrow the request.',
  },
  query_timeout: {
    retryable: true,
    message: 'The reporting query timed out. Try again.',
  },
  data_unavailable: {
    retryable: true,
    message: 'Reporting data is temporarily unavailable.',
  },
  chart_spec_invalid: {
    retryable: false,
    message: 'This request could not be represented as a supported report.',
  },
} as const
